/* =========================================================
   Routing.

   Screens keep the prototype's `go(screen, params)` call shape,
   but every navigation is now a real URL. react-router pushes
   each one onto the browser history, so Back / Forward walk the
   exact trail of visited screens — deep links and refresh work
   too, which the in-memory route object could not do.
   ========================================================= */
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type ScreenId =
  | 'login' | 'dashboard' | 'org' | 'sites' | 'site'
  | 'instructors' | 'instructorDetail' | 'users' | 'reports' | 'admin';

export interface RouteParams {
  orgId?: string;
  siteId?: string;
  instructorId?: string;
  /** Opens the nested disassociation-requests view inside Instructores. */
  disassoc?: boolean;
  [key: string]: string | boolean | undefined;
}

export interface Route extends RouteParams {
  screen: ScreenId;
}

export const PATHS = {
  login: '/login',
  dashboard: '/',
  org: '/organizaciones',
  sites: '/sites',
  instructors: '/instructores',
  users: '/usuarios',
  reports: '/reportes',
  admin: '/administracion',
} as const;

/** Build the URL for a screen + params pair. */
export function pathFor(screen: ScreenId, params: RouteParams = {}): string {
  const extra = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (k === 'orgId' || k === 'siteId' || k === 'instructorId') return;
    if (v === undefined || v === false) return;
    extra.set(k, v === true ? '1' : String(v));
  });
  const qs = extra.toString() ? `?${extra}` : '';

  switch (screen) {
    case 'login':
      return PATHS.login + qs;
    case 'org':
      return (params.orgId ? `${PATHS.org}/${encodeURIComponent(params.orgId)}` : PATHS.org) + qs;
    case 'sites':
    case 'site':
      return (params.siteId ? `${PATHS.sites}/${encodeURIComponent(params.siteId)}` : PATHS.sites) + qs;
    case 'instructors':
    case 'instructorDetail':
      return (
        (params.instructorId
          ? `${PATHS.instructors}/${encodeURIComponent(params.instructorId)}`
          : PATHS.instructors) + qs
      );
    case 'users':
      return PATHS.users + qs;
    case 'reports':
      return PATHS.reports + qs;
    case 'admin':
      return PATHS.admin + qs;
    case 'dashboard':
    default:
      return PATHS.dashboard + qs;
  }
}

/** Reverse of pathFor: the screen id + params for the current URL. */
export function routeFor(pathname: string, search: string): Route {
  const q = new URLSearchParams(search);
  const extras: RouteParams = {};
  q.forEach((v, k) => {
    extras[k] = v === '1' ? true : v;
  });

  const seg = pathname.split('/').filter(Boolean);
  const [head, id] = seg;
  const decoded = id ? decodeURIComponent(id) : undefined;

  switch (head) {
    case 'login':
      return { screen: 'login', ...extras };
    case 'organizaciones':
      return { screen: 'org', orgId: decoded, ...extras };
    case 'sites':
      return decoded
        ? { screen: 'site', siteId: decoded, ...extras }
        : { screen: 'sites', ...extras };
    case 'instructores':
      return decoded
        ? { screen: 'instructorDetail', instructorId: decoded, ...extras }
        : { screen: 'instructors', ...extras };
    case 'usuarios':
      return { screen: 'users', ...extras };
    case 'reportes':
      return { screen: 'reports', ...extras };
    case 'administracion':
      return { screen: 'admin', ...extras };
    default:
      return { screen: 'dashboard', ...extras };
  }
}

/** The current screen + params, in the shape the ported screens expect. */
export function useRoute(): Route {
  const { pathname, search } = useLocation();
  return useMemo(() => routeFor(pathname, search), [pathname, search]);
}

export type GoFn = (screen: ScreenId, params?: RouteParams) => void;

/** `go('site', { siteId })` — navigates and scrolls to top, as before. */
export function useGo(): GoFn {
  const navigate = useNavigate();
  return useCallback(
    (screen: ScreenId, params: RouteParams = {}) => {
      navigate(pathFor(screen, params));
      window.scrollTo(0, 0);
    },
    [navigate],
  );
}
